import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X, ZoomIn, Play, Grid3X3, Info, ArrowLeft } from 'lucide-react'

interface Image {
  id: string
  filename: string
  thumbnail: string
  fullsize: string
}

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  thumbnail: string
  images: Image[]
}

type GalleryData = Record<string, Gallery>

export const GalleryPage: React.FC = () => {
  const { galleryId } = useParams<{ galleryId: string }>()
  const [galleries, setGalleries] = useState<GalleryData>({})
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isSlideshow, setIsSlideshow] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch('/data/galleries.json')
        const data = await response.json()
        setGalleries(data)
      } catch (error) {
        console.error('Error fetching galleries:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGalleries()
  }, [])

  // Slideshow effect
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isSlideshow && lightboxOpen) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === gallery?.images.length - 1 ? 0 : prev + 1
        )
      }, 3000)
    }
    return () => clearInterval(interval)
  }, [isSlideshow, lightboxOpen])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        case 'Escape':
          closeLightbox()
          break
        case 't':
          setLightboxOpen(false)
          break
        case 'z':
          // Zoom functionality would go here
          break
        case 'p':
          setIsSlideshow(!isSlideshow)
          break
        case 'i':
          setShowInfo(!showInfo)
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [lightboxOpen, currentImageIndex, isSlideshow, showInfo])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  const gallery = galleryId ? galleries[galleryId] : null

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light text-gray-900 mb-4">Gallery not found</h1>
          <Link to="/portfolio" className="text-gray-600 hover:text-gray-900">
            ← Back to Portfolio
          </Link>
        </div>
      </div>
    )
  }

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
    setIsSlideshow(false)
    setShowInfo(false)
  }

  const goToNext = () => {
    setCurrentImageIndex((prev) => 
      prev === gallery.images.length - 1 ? 0 : prev + 1
    )
  }

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? gallery.images.length - 1 : prev - 1
    )
  }

  const currentImage = gallery.images[currentImageIndex]

  return (
    <div className="min-h-screen">
      {/* Gallery Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-4 tracking-wide">
              {gallery.title}
            </h1>
            <p className="text-xl text-gray-600 mb-2">{gallery.subtitle}</p>
            <p className="text-sm text-gray-500 uppercase tracking-wide">
              {gallery.category} • {gallery.location}
            </p>
            <div className="mt-6 text-sm text-gray-500">
              {gallery.images.length} {gallery.images.length === 1 ? 'image' : 'images'}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Controls */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-center space-x-6">
          <button 
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
            title="Thumbnails (t)"
          >
            <Grid3X3 className="w-4 h-4" />
            <span>Thumbnails</span>
          </button>
          <button 
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
            title="Zoom (z)"
          >
            <ZoomIn className="w-4 h-4" />
            <span>Zoom</span>
          </button>
          <button 
            onClick={() => {
              openLightbox(0)
              setIsSlideshow(true)
            }}
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
            title="Play Slideshow (p)"
          >
            <Play className="w-4 h-4" />
            <span>Play Slideshow</span>
          </button>
          <button 
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
            title="Gallery Info (i)"
          >
            <Info className="w-4 h-4" />
            <span>Info</span>
          </button>
        </div>
      </section>

      {/* Thumbnail Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {gallery.images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => openLightbox(index)}
                className="aspect-square overflow-hidden bg-gray-100 hover:shadow-lg transition-all duration-300 group"
              >
                <img
                  src={image.thumbnail}
                  alt={`${gallery.title} - Image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && currentImage && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Navigation Controls */}
          <div className="absolute top-4 right-4 z-10 flex space-x-2">
            <button
              onClick={() => setIsSlideshow(!isSlideshow)}
              className="p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
              title={isSlideshow ? 'Pause Slideshow (p)' : 'Play Slideshow (p)'}
            >
              <Play className={`w-5 h-5 ${isSlideshow ? 'opacity-100' : 'opacity-50'}`} />
            </button>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
              title="Toggle Info (i)"
            >
              <Info className="w-5 h-5" />
            </button>
            <button
              onClick={closeLightbox}
              className="p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Previous/Next Buttons */}
          {gallery.images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
                title="Previous (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors duration-200"
                title="Next (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div className="w-full h-full flex items-center justify-center p-16">
            <img
              src={currentImage.fullsize}
              alt={`${gallery.title} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            />
          </div>

          {/* Image Info */}
          {showInfo && (
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-4 rounded">
              <h3 className="text-lg font-light mb-2">{gallery.title}</h3>
              <p className="text-sm text-gray-300 mb-1">{gallery.subtitle}</p>
              <p className="text-xs text-gray-400">
                Image {currentImageIndex + 1} of {gallery.images.length} • {gallery.location}
              </p>
            </div>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} / {gallery.images.length}
          </div>
        </div>
      )}
    </div>
  )
}
